import { Box, Stack } from '@mui/material';
import React, { Suspense } from 'react';
import { Outlet, useNavigation } from 'react-router';

import { EmailWindow } from '@/components/emailWindow/EmailWindow.comp.tsx';
import { GlobalProgressBar } from '@/components/layout/routes/GlobalProgressBar.comp.tsx';
import { TopBar } from '@/components/layout/topBar/TopBar.comp.tsx';
import { TOP_BAR_HEIGHT, TOP_BAR_MARGIN } from '@/components/layout/topBar/TopBar.styles.ts';
import { MaintenanceModeNotice } from '@/components/maintenance/MaintenanceModeNotice.comp.tsx';
import { TeamAnnouncementsBar } from '@/components/teamAnnouncements/TeamAnnouncementsBar.comp.tsx';
import { LeftBarNavigation } from '@/components/ui/leftBarNavigation/LeftBarNavigation.comp.tsx';
import { MAX_WIDTH, SIDE_MENU_WIDTH } from '@/config/theme/theme.ts';
import { themeColors } from '@/config/theme/themeColors';
import { AnnouncementsProvider } from '@/contexts/announcements/AnnouncementsProvider.util.tsx';
import { DictionariesContextProvider } from '@/contexts/dictionaries/DictionariesContextProvider.util.tsx';
import { DraggableContext } from '@/contexts/draggableContext/Draggable.context.tsx';
import { InfoPanelProvider } from '@/contexts/infoPanel/InfoPanelProvider.util.tsx';
import { UserContextProvider } from '@/contexts/userContext/UserContextProvider.util.tsx';
import { UserContextVerifier } from '@/contexts/userContext/UserContextVerifier.util.tsx';
import { HistoryController } from '@/controlers/History.controller.tsx';
import { PostLoginController } from '@/controlers/PostLogin.controller.tsx';
import { SseController } from '@/controlers/Sse.controller.tsx';
import { EmailsContextProvider } from '@/views/emails/context/EmailsContextProvider.tsx';

const NavigationProgress: React.FC = () => {
  const { state } = useNavigation();
  return state === 'idle' ? null : <GlobalProgressBar />;
};

export const AppShell: React.FC = () => (
  <>
    <HistoryController />
    <PostLoginController />
    <UserContextProvider>
      <UserContextVerifier>
        <AnnouncementsProvider>
          <InfoPanelProvider>
            <DictionariesContextProvider>
              <EmailsContextProvider>
                <DraggableContext>
                  <SseController />
                  <Stack
                    sx={{
                      backgroundColor: 'primary.bg',
                      border: `1px solid ${themeColors.stroke}`,
                      height: '100vh',
                      margin: 'auto',
                      maxWidth: MAX_WIDTH,
                      position: 'relative',
                    }}
                  >
                    <TopBar />
                    <TeamAnnouncementsBar />
                    <MaintenanceModeNotice />
                    <Stack
                      direction="row"
                      id="main-stack"
                      sx={{
                        height: `calc(100vh - ${TOP_BAR_HEIGHT} - ${TOP_BAR_MARGIN})`,
                        width: '100%',
                      }}
                    >
                      <LeftBarNavigation />
                      <Box
                        component="main"
                        sx={{
                          height: `calc(100dvh - ${TOP_BAR_HEIGHT})`,
                          margin: 'auto',
                          overflowY: 'auto',
                          position: 'relative',
                          width: `calc(${MAX_WIDTH} - ${SIDE_MENU_WIDTH})`,
                        }}
                      >
                        <NavigationProgress />
                        <Suspense fallback={<GlobalProgressBar />}>
                          <Outlet />
                        </Suspense>
                      </Box>
                    </Stack>
                    <EmailWindow />
                  </Stack>
                </DraggableContext>
              </EmailsContextProvider>
            </DictionariesContextProvider>
          </InfoPanelProvider>
        </AnnouncementsProvider>
      </UserContextVerifier>
    </UserContextProvider>
  </>
);
